import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { TableTopPanel } from 'components/TableTopPanel';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { cnMixSpace } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCatalog } from '../CatalogList/hooks/useCatalog';
import {
    CatalogRatiosToolbar,
    defaultSet,
    useCatalogRatiosColumns,
    useCatalogRatiosData,
    useRemoveRatio,
} from '../Common/CatalogRatios';
import Styles from '../Common/CatalogRatios/catalogRatios.module.css';
import { CatalogRatiosHistory } from '../Common/CatalogRatios/components/CatalogRatiosHistory';
import { CatalogTablePage } from '../Common/CatalogTablePage';
import { CreateOrEditRatioModal } from '../Common/CreateOrEditRatioModal';
import type { RatioIdType } from '../Common/types';

export const CatalogRatios = () => {
    const [currentRatio, setCurrentRatio] = useState<RatioIdType>();
    const [isHistoryOpened, setIsHistoryOpened] = useState<boolean>(false);

    const { id } = useParams();
    const { catalogRatios: catalogsRules } = useAccess();

    const tableRef = useRef<AgGridReact | null>(null);
    const tableApi = useMemo(() => (tableRef && 'current' in tableRef ? tableRef.current : undefined), [tableRef]);

    const { catalogData, getCatalog } = useCatalog(id);

    const { catalogName, totalCount, fetchedItems, isError, dataSource, updateCatalogRatios } = useCatalogRatiosData({
        catalogId: id,
    });

    const handleOpenCreateOrEditDialog = useCallback(
        (ratioId: RatioIdType) => () => {
            setCurrentRatio(ratioId);
        },
        [],
    );

    const toggleHistory = useCallback(() => {
        setIsHistoryOpened((p) => !p);
    }, []);

    const onSuccessAction = useCallback(() => {
        getCatalog();
        updateCatalogRatios();
    }, [getCatalog, updateCatalogRatios]);

    const { isOpenConfirmationDialog, handleDelete, handleOpenRemoveDialog, handleCloseConfirmationDialog } =
        useRemoveRatio({
            onSuccessAction,
        });

    const { columns, isColumnVisible, tableSettingsConfirm, fullSetSettingsColumns } = useCatalogRatiosColumns({
        catalogRatiosRules: catalogsRules,
        onEdit: handleOpenCreateOrEditDialog,
        onRemove: handleOpenRemoveDialog,
    });

    const updatedAt = catalogData?.data?.updatedAt ?? '';

    return (
        <CatalogTablePage catalogName={catalogName} methodology={ProjectMethodologyEnum.Uer} isError={isError}>
            <CatalogRatiosToolbar
                updatedAt={updatedAt}
                handleOpenCreateOrEditDialog={handleOpenCreateOrEditDialog}
                handleOpenHistory={toggleHistory}
                catalogRatiosRules={catalogsRules}
            />
            <div
                className={cn(
                    Styles.table,
                    cnMixSpace({
                        mT: 'l',
                        mH: 'xl',
                    }),
                )}
            >
                <TableTopPanel
                    totalItems={totalCount}
                    fetchedItems={fetchedItems}
                    tableApi={tableApi}
                    optionalColumns={fullSetSettingsColumns}
                    isColumnVisible={isColumnVisible}
                    handleTableSettingsConfirm={tableSettingsConfirm}
                    showPinColumn
                />
                <AgGridTable
                    key="catalog-ratios"
                    ref={tableRef}
                    datasource={dataSource}
                    columnDefs={columns}
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    rowModelType="infinite"
                    defaultColDef={defaultSet}
                    context={{
                        tableUniqueId: 'catalogRatios',
                    }}
                    headerHeight={40}
                />
            </div>
            {isHistoryOpened && <CatalogRatiosHistory catalogId={id} closeHistory={toggleHistory} />}
            {typeof currentRatio !== 'undefined' && typeof id !== 'undefined' && (
                <CreateOrEditRatioModal
                    id={currentRatio}
                    onClose={handleOpenCreateOrEditDialog(undefined)}
                    refreshData={onSuccessAction}
                />
            )}
            {isOpenConfirmationDialog && (
                <ConfirmationDialog
                    isOpened
                    onSubmit={handleDelete}
                    onClose={handleCloseConfirmationDialog}
                    textHeader="Удаление коэффициента"
                    textConfirmation="Вы уверены, что хотите удалить коэффициент?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                />
            )}
        </CatalogTablePage>
    );
};

CatalogRatios.displayName = 'CatalogRatios';
